import { useState } from 'react';

import { useGetNtfyMessagesQuery, useGetNtfyTopicsQuery } from 'data/__generated__';

import { DataTable } from 'components/DataTable';
import { SegmentedControl } from 'components/SegmentedControl';
import { TotemSelect } from 'components/TotemSelect';

import { ntfyColumns, type NtfyMessageRow } from '../constants/ntfyColumns';
import { PublishNtfyTestMessageButton } from './PublishNtfyTestMessageButton';

/** Lecture « en direct » : ntfy ne pousse rien vers la totadmin, on relit son cache à ce rythme. */
const POLL_INTERVAL_MS = 10_000;

type NtfyWindow = '1h' | '6h' | '12h';

/** 12 h = la durée du cache ntfy : au-delà, il n'y a plus rien à lire (l'historique long est dans Loki). */
const WINDOW_OPTIONS: { value: NtfyWindow; label: string }[] = [
    { value: '1h', label: '1 h' },
    { value: '6h', label: '6 h' },
    { value: '12h', label: '12 h' },
];

export const NtfyMessagesList = () => {
    // Vide = tous les topics. Filtré CÔTÉ SERVEUR : le plafond de messages s'applique après ce filtre,
    // un topic bavard (`totem-billing`) ne peut donc pas masquer les autres quand on les isole.
    const [topics, setTopics] = useState<string[]>([]);
    const [since, setSince] = useState<NtfyWindow>('12h');

    const { data: topicsData, error: topicsError } = useGetNtfyTopicsQuery();
    const { data, loading, error, refetch } = useGetNtfyMessagesQuery({
        variables: { topics: topics.length ? topics : null, since },
        pollInterval: POLL_INTERVAL_MS,
        fetchPolicy: 'cache-and-network',
    });

    if (error || topicsError) {
        throw new Error('Une erreur est survenue lors de la lecture des notifications ntfy');
    }

    const allTopics = topicsData?.ntfyTopics ?? [];
    const result = data?.ntfyMessages;

    return (
        <div className="flex h-full w-full flex-col gap-2.5 overflow-hidden">
            {result?.error ? (
                <div
                    className="rounded-md bg-destructive/10 px-4 py-2.5 text-sm text-destructive"
                    data-test="ntfy-error"
                >
                    {result.error}
                </div>
            ) : null}
            {result?.isTruncated ? (
                <div className="rounded-md bg-amber-500/10 px-4 py-2.5 text-sm text-amber-700 dark:text-amber-400">
                    Seuls les {result.messages.length} messages les plus récents sont affichés : réduis la fenêtre ou
                    choisis des topics.
                </div>
            ) : null}
            <DataTable<NtfyMessageRow>
                columns={ntfyColumns}
                data={result?.messages ?? []}
                filterMode="client"
                getRowId={(row) => row.id}
                isLoading={loading && !data}
                searchPlaceholder="Titre, message, tags…"
                searchFilterKey="searchValue"
                emptyMessage="Aucun message sur la fenêtre choisie"
                toolbarActions={
                    <div className="flex items-center gap-2.5">
                        <TotemSelect<string>
                            isMulti
                            value={topics}
                            onChange={setTopics}
                            options={allTopics.map(({ name }) => ({ value: name, label: name }))}
                            placeholder="Tous les topics"
                            width="260px"
                            dataTest="ntfy-topics"
                        />
                        <SegmentedControl<NtfyWindow> options={WINDOW_OPTIONS} value={since} onChange={setSince} />
                        <PublishNtfyTestMessageButton
                            publishableTopics={allTopics.filter(({ canPublish }) => canPublish).map(({ name }) => name)}
                            onPublished={() => {
                                void refetch();
                            }}
                        />
                    </div>
                }
            />
        </div>
    );
};
